import { useCallback, useEffect, useState } from "react"
import { isEmpty } from "../utils/check"
import { useHotkey } from "./use-hotkey"

export const DEBOUNCE_ULTRA_SHORT_DELAY = 10
export const DEBOUNCE_SHORT_DELAY = 500
export const DEBOUNCE_NORMAL_DELAY = 1000
export const DEBOUNCE_LONG_DELAY = 2000
export const DEBOUNCE_XL_LONG_DELAY = 5000

/**
 * Hook to debounce callbacks, mainly searches, but immediate if pressed on Enter key
 */
export const useDebounce = <T>(value: T, pDelay: number = DEBOUNCE_NORMAL_DELAY) => {
	const [debouncedValue, setDebouncedValue] = useState<T>(value)
	const [timerId, setTimerId] = useState<NodeJS.Timeout>()

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedValue(value)
		}, pDelay)
		setTimerId(timer)
		return () => {
			clearTimeout(timer)
			setTimerId(undefined)
		}
	}, [value, pDelay])

	// immediately return value if enter is pressed
	useHotkey(
		"Enter,Escape",
		useCallback(
			(_event, hotkey) => {
				clearTimeout(timerId)
				if (hotkey === "Enter") setDebouncedValue(value)
			},
			[timerId, value],
		),
	)
	// immediately return value is no value is entered
	useEffect(() => {
		if (isEmpty(value)) {
			clearTimeout(timerId)
			setDebouncedValue(value)
		}
	}, [value, timerId])

	const status = { pending: value !== debouncedValue }
	return [debouncedValue, status] as const
}

/**
 * Hook to debounce callbacks, mainly searches, but immediate if pressed on Enter key
 */
export const useRevampedDebounce = <T>(value: T, pDelay: number = DEBOUNCE_NORMAL_DELAY) => {
	const [debouncedValue] = useDebounce(value, pDelay)
	return debouncedValue
}
